import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import Form from '../Form';
import Tooltip from '../Tooltip';

import './styles.css';

const messages = defineMessages({
  tooltip: {
    id: 'hubs.overview.emailer.tooltip',
    defaultMessage:
      'This is the email specified in your Naviance Student Profile. If you would like to be contacted at another email or by phone, please note that in the message body.',
  },
  to: {
    id: 'hubs.overview.emailer.to',
    defaultMessage: 'To:',
  },
  from: {
    id: 'hubs.overview.emailer.from',
    defaultMessage: 'From:',
  },
  subject: {
    id: 'hubs.overview.emailer.subject',
    defaultMessage: 'Subject:',
  },
  body: {
    id: 'hubs.overview.emailer.body',
    defaultMessage: 'Body:',
  },
  phone: {
    id: 'hubs.overview.emailer.phone',
    defaultMessage: 'Phone:',
  },
  fax: {
    id: 'hubs.overview.emailer.fax',
    defaultMessage: 'Fax:',
  },
  email: {
    id: 'hubs.overview.emailer.email',
    defaultMessage: 'Email:',
  },
  address: {
    id: 'hubs.overview.emailer.address',
    defaultMessage: 'Address:',
  },
  attn: {
    id: 'hubs.overview.emailer.attn',
    defaultMessage: 'Attn: Admissions:',
  },
});

class Emailer extends React.PureComponent {
  static propTypes = {
    admissionsOffice: PropTypes.object,
    userEmail: PropTypes.string,
    onSubjectChange: PropTypes.func,
    onBodyChange: PropTypes.func,
    intl: PropTypes.object,
    showAddress: PropTypes.bool,
  };

  handleSubjectChange = e => {
    const {
      target,
      target: { value },
    } = e;

    if (value.trim() === '') {
      target.value = '';
      target.checkValidity();
    } else {
      this.props.onSubjectChange(value);
    }
  };

  handleBodyChange = e => {
    const {
      target,
      target: { value },
    } = e;

    if (value.trim() === '') {
      target.value = '';
      target.checkValidity();
    } else {
      this.props.onBodyChange(value);
    }
  };

  render() {
    const { userEmail, admissionsOffice, intl, showAddress } = this.props;

    let toEmail;
    let fromEmail;
    let tooltipMessage;
    let toPhone;
    let toFax;
    let toAddressName;
    let toAddress1;
    let toAddress2;
    let toAddress3;
    let toAddress4;
    const tooltipRef = React.createRef();

    /* istanbul ignore else */
    if (admissionsOffice) {
      toEmail = get(admissionsOffice, 'email.value');
      fromEmail = userEmail;
      toPhone = get(admissionsOffice, 'phoneNumber.value');
      toFax = get(admissionsOffice, 'fax.value');

      tooltipMessage = fromEmail && intl.formatMessage(messages.tooltip);

      toAddressName = intl.formatMessage(messages.attn);
      toAddress1 = get(admissionsOffice, 'street1.value');
      toAddress2 = get(admissionsOffice, 'street2.value', null);
      toAddress3 = `
        ${get(admissionsOffice, 'city.value')} ${get(
        admissionsOffice,
        'state.value',
      )} ${get(admissionsOffice, 'zip.value')}
      `;
      toAddress4 = get(admissionsOffice, 'country.value');
    }

    return (
      <div className="emailer">
        <div className="email-fields">
          <Form.Field>
            <label tabIndex={0}>
              <FormattedMessage {...messages.to} />
            </label>
            <span tabIndex={0}>{toEmail}</span>
          </Form.Field>
          <Form.Field>
            <label tabIndex={0}>
              <FormattedMessage {...messages.from} />
            </label>
            <span tabIndex={0}>{fromEmail}</span>
            {tooltipMessage && (
              <Tooltip
                popupClassName="emailer-tooltip"
                contentText={tooltipMessage}
                showCloseBtn
                onClose={() => {
                  tooltipRef.current.focus();
                }}
              >
                <i
                  className="info circle icon heading-tooltip-icon"
                  tabIndex="0"
                  aria-label="Tooltip"
                  ref={tooltipRef}
                />
              </Tooltip>
            )}
          </Form.Field>
        </div>
        <div className="field-groups">
          <section>
            <Form.Field>
              <label tabIndex={0}>
                <FormattedMessage {...messages.subject} />
              </label>
              <input
                type="text"
                className="subject"
                maxLength="256"
                required
                onChange={this.handleSubjectChange}
              />
            </Form.Field>
            <Form.Field>
              <label tabIndex={0}>
                <FormattedMessage {...messages.body} />
              </label>
              <textarea maxLength="1024" onChange={this.handleBodyChange} required />
            </Form.Field>
          </section>
          <aside>
            <Form.Field>
              <label tabIndex={0}>
                <FormattedMessage {...messages.phone} />
              </label>
              {toPhone && <a href={`tel:${toPhone}`}>{toPhone}</a>}
            </Form.Field>
            <Form.Field>
              <label tabIndex={0}>
                <FormattedMessage {...messages.fax} />
              </label>
              <span tabIndex={0}>{toFax}</span>
            </Form.Field>
            <Form.Field>
              <label tabIndex={0}>
                <FormattedMessage {...messages.email} />
              </label>
              {toEmail && <a href={`mailto:${toEmail}`}>{toEmail}</a>}
            </Form.Field>
            {showAddress ? (
              <Form.Field className="address">
                <label tabIndex={0}>
                  <FormattedMessage {...messages.address} />
                </label>
                <ul>
                  <li tabIndex={0}>{toAddressName}</li>
                  <li tabIndex={0}>{toAddress1}</li>
                  {toAddress2 && <li tabIndex={0}>{toAddress2}</li>}
                  <li tabIndex={0}>{toAddress3}</li>
                  <li tabIndex={0}>{toAddress4}</li>
                </ul>
              </Form.Field>
            ) : null}
          </aside>
        </div>
      </div>
    );
  }
}

export default injectIntl(Emailer);
